const ORDER_STEPS = Object.freeze(['details', 'recipients', 'giving', 'review'])

function currency (amount) {
  const value = Number(amount) || 0
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2
  }).format(value)
}

function valueOf (element) {
  return element ? element.value.trim() : ''
}

function numberOf (element) {
  const value = Number(element ? element.value : 0)
  return Number.isFinite(value) && value > 0 ? value : 0
}

function setText (root, selector, value) {
  const element = root.querySelector(selector)
  if (element) element.textContent = value
}

function setSummaryRow (root, rowSelector, valueSelector, amount, label) {
  const row = root.querySelector(rowSelector)
  if (!row) return

  row.hidden = amount <= 0
  setText(row, valueSelector, label || currency(amount))
}

function initializeLanding () {
  document.querySelectorAll('[data-order-page]').forEach(page => {
    if (page.dataset.landingInitialized === 'true') return
    page.dataset.landingInitialized = 'true'

    const workspace = page.querySelector('#order-workspace')
    const access = page.querySelector('#order-access')

    page.querySelectorAll('[data-start-order]').forEach(button => {
      button.addEventListener('click', () => {
        if (!workspace) return
        const form = workspace.querySelector('[data-order-form]')
        if (form) form.classList.add('has-started')
        workspace.scrollIntoView({ behavior: 'smooth', block: 'start' })
        window.setTimeout(() => {
          const firstField = workspace.querySelector('[data-detail-field]:not([readonly])')
          if (firstField) firstField.focus({ preventScroll: true })
        }, 450)
      })
    })

    page.querySelectorAll('[data-show-order-access]').forEach(button => {
      button.addEventListener('click', () => {
        if (!access) return
        access.classList.add('is-emphasized')
        access.scrollIntoView({ behavior: 'smooth', block: 'center' })
        window.setTimeout(() => {
          const email = access.querySelector('input[type="email"]')
          if (email) email.focus({ preventScroll: true })
        }, 450)
      })
    })
  })
}

function initializeOrderForm (form) {
  if (form.dataset.initialized === 'true') return
  form.dataset.initialized = 'true'
  form.classList.add('is-enhanced')

  const configElement = form.querySelector('[data-order-form-config]')
  if (!configElement) return

  const config = JSON.parse(configElement.textContent)
  const workspace = form.closest('.mm-order-workspace')
  const panels = Array.from(form.querySelectorAll('[data-order-step]'))
  const stepLinks = Array.from(form.querySelectorAll('[data-step-link]'))
  const sendToAll = form.querySelector('[data-send-to-all]')
  const sponsorshipTiers = Array.from(form.querySelectorAll('[data-sponsorship-tier]'))
  const recipientCheckboxes = Array.from(form.querySelectorAll('[data-recipient-checkbox]'))
  const selectAllRecipients = form.querySelector('[data-select-all-recipients]')
  const corporateCheckbox = form.querySelector('[data-corporate-sponsorship]')
  const corporateName = form.querySelector('[data-corporate-name]')
  const householdRecipient = form.querySelector('[data-household-recipient]')
  const reciprocity = form.querySelector('[data-reciprocity]')
  const customList = form.querySelector('[data-custom-recipient-list]')
  const customTemplate = form.querySelector('#custom-recipient-template')
  const paymentTypeField = form.querySelector('#sender_payment_type')
  const loading = form.querySelector('#form-loading')
  const mobileNext = form.querySelector('[data-mobile-next]')
  const protectedSponsor = sendToAll && sendToAll.dataset.protected === 'true'
  let currentStep = 'details'
  let alphaFilter = 'all'
  let customIndex = 0
  let submitting = false

  if (config.editingOrder || form.dataset.hasErrors === 'true' || window.location.hash === '#order-workspace') {
    form.classList.add('has-started')
  }

  const selectedSponsorshipAmount = () => {
    const selected = sponsorshipTiers.find(input => input.checked)
    if (!selected) return 0
    const tier = config.sponsorshipTiers[selected.value]
    return tier ? Number(tier.amount) : 0
  }

  const protectedSponsorshipAmount = protectedSponsor ? selectedSponsorshipAmount() : 0

  function scrollToWorkspace () {
    if (!workspace) return
    const top = workspace.getBoundingClientRect().top + window.scrollY - 92
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
  }

  function validateDetails () {
    const name = form.querySelector('#sender_name')
    const email = form.querySelector('#sender_email')
    const phone = form.querySelector('#sender_phone')
    const fields = [name, email, phone].filter(Boolean)
    let valid = true

    fields.forEach(field => field.setCustomValidity(''))
    if (householdRecipient) householdRecipient.setCustomValidity('')
    fields.forEach(field => {
      if (!field.checkValidity()) {
        field.classList.add('is-invalid')
        valid = false
      } else {
        field.classList.remove('is-invalid')
      }
    })

    if (reciprocity && reciprocity.checked && valueOf(householdRecipient).length === 0) {
      householdRecipient.setCustomValidity('Choose your household from the regular recipient list to use reciprocity.')
      householdRecipient.classList.add('is-invalid')
      valid = false
    } else if (householdRecipient) {
      householdRecipient.classList.remove('is-invalid')
    }

    if (!valid) {
      const firstInvalid = [...fields, householdRecipient].filter(Boolean).find(field => !field.checkValidity())
      if (firstInvalid) firstInvalid.reportValidity()
    }
    return valid
  }

  function meaningfulCustomEntries () {
    return Array.from(form.querySelectorAll('[data-custom-recipient]')).filter(entry => {
      const existing = entry.dataset.existing === 'true'
      const name = valueOf(entry.querySelector('[data-custom-name]'))
      const address = valueOf(entry.querySelector('[data-custom-address]'))
      return existing || name.length > 0 || address.length > 0
    })
  }

  function validateCustomRecipients () {
    let valid = true
    let firstInvalid = null

    Array.from(form.querySelectorAll('[data-custom-recipient]')).forEach(entry => {
      const existing = entry.dataset.existing === 'true'
      const name = entry.querySelector('[data-custom-name]')
      const address = entry.querySelector('[data-custom-address]')
      const error = entry.querySelector('[data-custom-error]')
      const nameValue = valueOf(name)
      const addressValue = valueOf(address)
      const active = existing || nameValue.length > 0 || addressValue.length > 0
      const entryValid = !active || (nameValue.length > 0 && addressValue.length > 0)

      entry.classList.toggle('has-error', !entryValid)
      if (error) error.textContent = entryValid ? '' : 'Enter both a recipient name and a full delivery address.'
      if (!entryValid) {
        valid = false
        firstInvalid = firstInvalid || (nameValue.length === 0 ? name : address)
      }
    })

    if (!valid && firstInvalid) firstInvalid.focus()
    return valid
  }

  function validateCorporateSponsorship () {
    if (!corporateCheckbox || !corporateCheckbox.checked || !corporateName) return true

    const valid = valueOf(corporateName).length > 0
    corporateName.classList.toggle('is-invalid', !valid)
    if (!valid) {
      corporateName.setCustomValidity('Enter the company or entity name for recognition.')
      corporateName.reportValidity()
    } else {
      corporateName.setCustomValidity('')
    }
    return valid
  }

  function canLeaveStep (step, targetStep) {
    const movingForward = ORDER_STEPS.indexOf(targetStep) > ORDER_STEPS.indexOf(step)
    if (!movingForward) return true
    if (step === 'details') return validateDetails()
    if (step === 'recipients') return validateCustomRecipients()
    if (step === 'giving') return validateCorporateSponsorship()
    return true
  }

  function goToStep (step, options = {}) {
    if (!ORDER_STEPS.includes(step)) return false
    if (!options.force && !canLeaveStep(currentStep, step)) return false

    currentStep = step
    const currentIndex = ORDER_STEPS.indexOf(step)
    panels.forEach(panel => {
      const active = panel.dataset.orderStep === step
      panel.classList.toggle('is-active', active)
      panel.setAttribute('aria-hidden', active ? 'false' : 'true')
    })
    stepLinks.forEach(link => {
      const linkIndex = ORDER_STEPS.indexOf(link.dataset.stepLink)
      const active = link.dataset.stepLink === step
      link.classList.toggle('is-active', active)
      link.classList.toggle('is-complete', linkIndex < currentIndex)
      link.setAttribute('aria-current', active ? 'step' : 'false')
    })
    updateMobileAction()
    updateReview()
    if (options.scroll !== false) scrollToWorkspace()
    return true
  }

  function setRecipientMode (mode, options = {}) {
    if (mode === 'individual' && protectedSponsor) return

    form.querySelectorAll('[data-recipient-mode]').forEach(button => {
      const active = button.dataset.recipientMode === mode
      button.classList.toggle('is-active', active)
      button.setAttribute('aria-pressed', active ? 'true' : 'false')
    })
    form.querySelectorAll('[data-recipient-panel]').forEach(panel => {
      const active = panel.dataset.recipientPanel === mode
      panel.classList.toggle('is-active', active)
      panel.hidden = !active
    })

    if (sendToAll && options.syncToggle !== false) {
      sendToAll.checked = mode === 'sponsor'
    }
    updateRecipientSelectability()
    updateTotals()
  }

  function updateRecipientSelectability () {
    const sponsoring = Boolean(sendToAll && sendToAll.checked)

    sponsorshipTiers.forEach(input => {
      const amount = Number(input.dataset.amount) || 0
      input.disabled = !sponsoring || amount < protectedSponsorshipAmount
    })

    recipientCheckboxes.forEach(input => {
      const existing = input.dataset.existing === 'true'
      if (existing) {
        input.checked = true
        input.disabled = true
      } else {
        input.disabled = sponsoring
      }
      const card = input.closest('[data-recipient-card]')
      if (card) {
        card.classList.toggle('is-selected', input.checked)
        card.classList.toggle('is-unavailable', sponsoring && !existing)
      }
    })

    updateSelectAllControl()
  }

  function allRecipientsSelected () {
    return recipientCheckboxes.length > 0 && recipientCheckboxes.every(input => input.checked)
  }

  function updateSelectAllControl () {
    if (!selectAllRecipients) return

    const sponsoring = Boolean(sendToAll && sendToAll.checked)
    const selectedCount = recipientCheckboxes.filter(input => input.checked).length
    const allSelected = allRecipientsSelected()
    const editableRecipients = recipientCheckboxes.filter(input => input.dataset.existing !== 'true')
    const hasEditableSelection = editableRecipients.some(input => input.checked)

    selectAllRecipients.hidden = recipientCheckboxes.length === 0
    selectAllRecipients.disabled = sponsoring || editableRecipients.length === 0
    selectAllRecipients.setAttribute('aria-pressed', allSelected ? 'true' : 'false')
    setText(
      selectAllRecipients,
      '[data-select-all-label]',
      allSelected
        ? (hasEditableSelection ? (config.editingOrder ? 'Clear new selections' : 'Clear all selections') : 'All households selected')
        : 'Select all households'
    )

    let status = ''
    if (allSelected) {
      status = `All ${recipientCheckboxes.length} listed households are selected.`
    } else if (selectedCount > 0) {
      status = `${selectedCount} of ${recipientCheckboxes.length} listed households selected.`
    }
    setText(form, '[data-select-all-status]', status)
  }

  function toggleAllRecipients () {
    const shouldSelect = !allRecipientsSelected()
    recipientCheckboxes.forEach(input => {
      if (input.dataset.existing !== 'true' && !input.disabled) input.checked = shouldSelect
    })
    updateTotals()
  }

  function filterRecipients () {
    const search = valueOf(form.querySelector('[data-recipient-search]')).toLowerCase()
    const cards = Array.from(form.querySelectorAll('[data-recipient-card]'))
    let visible = 0

    cards.forEach(card => {
      const initial = card.dataset.initial || ''
      const nameMatches = !search || (card.dataset.name || '').includes(search)
      const alphaMatches = alphaFilter === 'all' ||
        (alphaFilter === 'N-Z' ? initial >= 'N' : initial === alphaFilter)
      const show = nameMatches && alphaMatches
      card.hidden = !show
      if (show) visible += 1
    })

    const status = form.querySelector('[data-search-status]')
    if (status) {
      if (search || alphaFilter !== 'all') {
        status.textContent = visible === 1 ? '1 household shown' : `${visible} households shown`
      } else {
        status.textContent = ''
      }
    }
  }

  function addCustomRecipient () {
    if (!customTemplate || !customList) return
    customIndex += 1
    const uniqueIndex = `new_${Date.now()}_${customIndex}`
    const wrapper = document.createElement('div')
    wrapper.innerHTML = customTemplate.innerHTML.replace(/NEW_RECORD/g, uniqueIndex).trim()
    const entry = wrapper.firstElementChild
    if (!entry) return
    customList.appendChild(entry)
    entry.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const firstField = entry.querySelector('[data-custom-name]')
    if (firstField) window.setTimeout(() => firstField.focus({ preventScroll: true }), 350)
    updateTotals()
  }

  function inferShipping (addressField) {
    if (!config.outOfTownEnabled || !addressField) return
    const entry = addressField.closest('[data-custom-recipient]')
    const checkbox = entry && entry.querySelector('[data-custom-out-of-town]')
    if (!checkbox || checkbox.disabled || checkbox.dataset.protected === 'true') return

    const address = valueOf(addressField)
    if (!address) return
    const local = config.localPostalCodePrefixes.some(prefix => address.includes(prefix))
    checkbox.checked = !local
    updateTotals()
  }

  function toggleCorporateFields () {
    if (!corporateCheckbox || !corporateName) return
    const enabled = corporateCheckbox.checked && config.corporateSponsorshipEnabled
    corporateName.disabled = !enabled
    if (!enabled) {
      corporateName.setCustomValidity('')
      corporateName.classList.remove('is-invalid')
    }
  }

  function giftValues () {
    return Array.from(form.querySelectorAll('.charitable-gift-input')).map(input => ({
      key: input.dataset.giftKey,
      label: input.dataset.giftLabel,
      amount: numberOf(input)
    }))
  }

  function calculate () {
    const sponsoring = Boolean(sendToAll && sendToAll.checked)
    const recipientCount = recipientCheckboxes.filter(input => input.checked).length
    const recipientTotal = sponsoring
      ? 0
      : Math.min(recipientCount * config.standardRecipient, config.standardRecipientCap)
    const customEntries = meaningfulCustomEntries()
    const customCount = customEntries.length
    const outOfTownCount = customEntries.filter(entry => {
      const checkbox = entry.querySelector('[data-custom-out-of-town]')
      return checkbox && checkbox.checked
    }).length
    const customTotal = customCount * config.customRecipient
    const shippingTotal = outOfTownCount * config.outOfTownSurcharge
    const sponsorshipTotal = sponsoring ? selectedSponsorshipAmount() : 0
    const surchargeTotal = !sponsoring && (recipientCount > 0 || customCount > 0)
      ? config.firstBasketSurcharge
      : 0
    const gifts = giftValues()
    const giftTotal = gifts.reduce((sum, gift) => sum + gift.amount, 0)
    const corporateTotal = corporateCheckbox && corporateCheckbox.checked
      ? config.corporateSponsorship
      : 0
    const total = recipientTotal + customTotal + shippingTotal + sponsorshipTotal +
      surchargeTotal + giftTotal + corporateTotal
    const due = Math.max(total - config.checkoutBaseline, 0)
    const reciprocitySelected = Boolean(reciprocity && reciprocity.checked)

    return {
      sponsoring,
      recipientCount,
      allRecipientsSelected: allRecipientsSelected(),
      recipientTotal,
      customCount,
      customTotal,
      outOfTownCount,
      shippingTotal,
      sponsorshipTotal,
      surchargeTotal,
      gifts,
      giftTotal,
      corporateTotal,
      total,
      due,
      reciprocitySelected
    }
  }

  function updateReview (calculation = calculate()) {
    const name = valueOf(form.querySelector('#sender_name')) || '—'
    const email = valueOf(form.querySelector('#sender_email'))
    const phone = valueOf(form.querySelector('#sender_phone'))
    setText(form, '[data-review-name]', name)
    setText(form, '[data-review-contact]', [email, phone].filter(Boolean).join(' · ') || '—')

    let recipients = 'None selected'
    if (calculation.sponsoring) {
      const selectedTier = sponsorshipTiers.find(input => input.checked)
      const tier = selectedTier && config.sponsorshipTiers[selectedTier.value]
      recipients = tier ? `${tier.name} full-list sponsorship` : 'Full community list sponsorship'
    } else if (calculation.allRecipientsSelected) {
      recipients = `Entire regular list (${calculation.recipientCount} ${calculation.recipientCount === 1 ? 'household' : 'households'})`
    } else if (calculation.recipientCount > 0) {
      recipients = `${calculation.recipientCount} community ${calculation.recipientCount === 1 ? 'household' : 'households'}`
    }
    setText(form, '[data-review-recipients]', recipients)
    setText(form, '[data-review-custom]', calculation.customCount > 0
      ? `${calculation.customCount} custom-address ${calculation.customCount === 1 ? 'basket' : 'baskets'}${calculation.outOfTownCount > 0 ? ` · ${calculation.outOfTownCount} shipped` : ''}`
      : '')
    setText(form, '[data-review-reciprocity]', calculation.reciprocitySelected
      ? 'Reciprocity is on · final regular-list additions and any balance are calculated after close'
      : '')

    const giving = calculation.gifts.filter(gift => gift.amount > 0)
    setText(form, '[data-review-giving]', giving.length > 0
      ? giving.map(gift => `${gift.label}: ${currency(gift.amount)}`).join(' · ')
      : 'No additional gifts')
    setText(form, '[data-review-corporate]', calculation.corporateTotal > 0
      ? `Corporate sponsor: ${valueOf(corporateName) || 'name needed'}`
      : '')
    setText(form, '[data-review-total]', currency(calculation.total))
    setText(form, '[data-review-due]', currency(calculation.due))
  }

  function updateTotals () {
    updateRecipientSelectability()
    const calculation = calculate()
    const displayedRecipientCount = calculation.sponsoring ? recipientCheckboxes.length : calculation.recipientCount
    setText(form, '[data-selected-count]', displayedRecipientCount)
    setText(
      form,
      '[data-selection-label]',
      calculation.sponsoring
        ? ' households · full list'
        : (calculation.allRecipientsSelected ? ' selected · all households' : ' selected')
    )
    setText(form, '[data-order-total]', currency(calculation.total))
    setText(form, '[data-amount-due]', currency(calculation.due))

    const uncappedRecipientTotal = calculation.recipientCount * config.standardRecipient
    let recipientLabel = calculation.recipientCount > 0
      ? `${calculation.recipientCount} × ${currency(config.standardRecipient)}`
      : '—'
    if (uncappedRecipientTotal > config.standardRecipientCap) {
      recipientLabel = `${calculation.recipientCount} households · ${currency(config.standardRecipientCap)} cap`
    } else if (calculation.allRecipientsSelected) {
      recipientLabel = `All ${calculation.recipientCount} · ${recipientLabel}`
    }
    setSummaryRow(form, '[data-summary-recipients-row]', '[data-summary-recipients]', calculation.recipientTotal, recipientLabel)
    setSummaryRow(form, '[data-summary-custom-row]', '[data-summary-custom]', calculation.customTotal,
      calculation.customCount > 0 ? `${calculation.customCount} × ${currency(config.customRecipient)}` : '—')
    setSummaryRow(form, '[data-summary-shipping-row]', '[data-summary-shipping]', calculation.shippingTotal,
      calculation.outOfTownCount > 0 ? `${calculation.outOfTownCount} × ${currency(config.outOfTownSurcharge)}` : '—')
    setSummaryRow(form, '[data-summary-sponsorship-row]', '[data-summary-sponsorship]', calculation.sponsorshipTotal)
    setSummaryRow(form, '[data-summary-corporate-row]', '[data-summary-corporate]', calculation.corporateTotal)
    setSummaryRow(form, '[data-summary-surcharge-row]', '[data-summary-surcharge]', calculation.surchargeTotal)
    calculation.gifts.forEach(gift => {
      setSummaryRow(
        form,
        `[data-summary-gift-row="${gift.key}"]`,
        `[data-summary-gift="${gift.key}"]`,
        gift.amount
      )
    })

    form.querySelectorAll('.checkout').forEach(button => {
      button.disabled = config.editingOrder ? calculation.due <= 0 : calculation.total <= 0
    })
    const saveButton = form.querySelector('#save_without_checkout')
    if (saveButton) {
      const canSaveWithoutCheckout = calculation.due <= 0 && (config.editingOrder || calculation.reciprocitySelected)
      saveButton.hidden = !canSaveWithoutCheckout
    }

    setText(form, '[data-mobile-total-value]', currency(config.editingOrder ? calculation.due : calculation.total))
    updateReview(calculation)
    updateMobileAction()
  }

  function updateMobileAction () {
    if (!mobileNext) return
    const index = ORDER_STEPS.indexOf(currentStep)
    if (currentStep === 'review') {
      mobileNext.innerHTML = 'Choose payment <span aria-hidden="true">↓</span>'
    } else {
      const nextLabel = ORDER_STEPS[index + 1] === 'review' ? 'Review order' : 'Continue'
      mobileNext.innerHTML = `${nextLabel} <span aria-hidden="true">→</span>`
    }
  }

  function submitOrder (paymentType) {
    if (submitting) return
    if (!validateDetails()) {
      goToStep('details', { force: true })
      return
    }
    if (!validateCustomRecipients()) {
      goToStep('recipients', { force: true })
      return
    }
    if (!validateCorporateSponsorship()) {
      goToStep('giving', { force: true })
      return
    }

    const calculation = calculate()
    if (paymentType === 'none' && calculation.due > 0) return
    if (paymentType !== 'none' && calculation.due <= 0 && config.editingOrder) return
    if (!config.editingOrder && calculation.total <= 0 && !calculation.reciprocitySelected) return

    submitting = true
    paymentTypeField.value = paymentType
    form.classList.add('is-submitting')
    if (loading) loading.hidden = false
    form.querySelectorAll('[data-payment-type]').forEach(button => { button.disabled = true })
    window.HTMLFormElement.prototype.submit.call(form)
  }

  form.addEventListener('input', event => {
    if (event.target.matches('[data-detail-field], [data-custom-name], [data-custom-address], .charitable-gift-input, [data-corporate-name]')) {
      if (event.target.dataset.customName !== undefined || event.target.dataset.customAddress !== undefined) {
        const entry = event.target.closest('[data-custom-recipient]')
        if (entry) {
          entry.classList.remove('has-error')
          const error = entry.querySelector('[data-custom-error]')
          if (error) error.textContent = ''
        }
      }
      updateTotals()
    }
  })

  form.addEventListener('change', event => {
    if (event.target === sendToAll && protectedSponsor && !sendToAll.checked) {
      sendToAll.checked = true
      return
    }
    if (event.target.matches('[data-recipient-checkbox], [data-sponsorship-tier], [data-send-to-all], [data-custom-out-of-town], [data-corporate-sponsorship], [data-household-recipient], [data-reciprocity]')) {
      if (event.target === corporateCheckbox) toggleCorporateFields()
      if (event.target === sendToAll) setRecipientMode(sendToAll.checked ? 'sponsor' : 'individual', { syncToggle: false })
      updateTotals()
    }
  })

  form.addEventListener('focusout', event => {
    if (event.target.matches('[data-custom-address]')) inferShipping(event.target)
    if (event.target.matches('[data-detail-field]')) validateDetails()
  })

  form.addEventListener('click', event => {
    const next = event.target.closest('[data-next-step]')
    if (next) {
      goToStep(next.dataset.nextStep)
      return
    }
    const previous = event.target.closest('[data-previous-step]')
    if (previous) {
      goToStep(previous.dataset.previousStep)
      return
    }
    const edit = event.target.closest('[data-edit-step]')
    if (edit) {
      goToStep(edit.dataset.editStep, { force: true })
      return
    }
    const stepLink = event.target.closest('[data-step-link]')
    if (stepLink) {
      goToStep(stepLink.dataset.stepLink)
      return
    }
    const recipientMode = event.target.closest('[data-recipient-mode]')
    if (recipientMode) {
      setRecipientMode(recipientMode.dataset.recipientMode)
      return
    }
    const selectAll = event.target.closest('[data-select-all-recipients]')
    if (selectAll) {
      toggleAllRecipients()
      return
    }
    const alpha = event.target.closest('[data-alpha]')
    if (alpha) {
      alphaFilter = alpha.dataset.alpha
      form.querySelectorAll('[data-alpha]').forEach(button => button.classList.toggle('is-active', button === alpha))
      filterRecipients()
      return
    }
    const addCustom = event.target.closest('[data-add-custom-recipient]')
    if (addCustom) {
      addCustomRecipient()
      return
    }
    const removeCustom = event.target.closest('[data-remove-custom-recipient]')
    if (removeCustom) {
      const entry = removeCustom.closest('[data-custom-recipient]')
      if (entry && entry.dataset.existing !== 'true') entry.remove()
      updateTotals()
      return
    }
    const giftPreset = event.target.closest('[data-gift-preset]')
    if (giftPreset) {
      const input = form.querySelector(`#${giftPreset.dataset.giftTarget}`)
      if (input) {
        const minimum = Number(input.min) || 0
        input.value = Math.max(Number(giftPreset.dataset.giftPreset), minimum).toFixed(2)
        input.dispatchEvent(new Event('input', { bubbles: true }))
      }
      return
    }
    const payment = event.target.closest('[data-payment-type]')
    if (payment) {
      submitOrder(payment.dataset.paymentType)
    }
  })

  const search = form.querySelector('[data-recipient-search]')
  if (search) search.addEventListener('input', filterRecipients)

  if (mobileNext) {
    mobileNext.addEventListener('click', () => {
      if (currentStep === 'review') {
        const payment = form.querySelector('.mm-payment-section')
        if (payment) payment.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
      const index = ORDER_STEPS.indexOf(currentStep)
      goToStep(ORDER_STEPS[index + 1])
    })
  }

  form.addEventListener('submit', event => {
    if (!submitting) {
      event.preventDefault()
      goToStep('review')
    }
  })

  if (protectedSponsor) {
    const individualMode = form.querySelector('[data-recipient-mode="individual"]')
    if (individualMode) {
      individualMode.disabled = true
      individualMode.title = 'This paid sponsorship is protected.'
    }
  }

  toggleCorporateFields()
  setRecipientMode(sendToAll && sendToAll.checked ? 'sponsor' : 'individual', { syncToggle: false })
  filterRecipients()
  updateTotals()
  goToStep('details', { force: true, scroll: false })
}

function initializeOrderExperience () {
  initializeLanding()
  document.querySelectorAll('[data-order-form]').forEach(initializeOrderForm)
}

document.addEventListener('turbolinks:load', initializeOrderExperience)
document.addEventListener('DOMContentLoaded', initializeOrderExperience)
